import { useMutation, useQueryClient } from '@tanstack/react-query';

import type { ServicesProvider } from '@ekhodealer/ekho-common';

import { useCurrentServicesProviderId } from '@common/hooks/useCurrentServicesProviderId';
import { apiClient } from '@config/server/apiClient';
import { configurationEndpoints } from '@config/server/apiEndpoints';
import { queries } from '@config/server/queryKeys';

export interface UpdateOnboardingPageParams {
	page: number;
	onboardingCompleted?: boolean;
}

/**
 * API function to update the onboarding page for a services provider (supports both forward and backward navigation)
 */
export const updateOnboardingPage = async (
	servicesProviderId: string,
	params: UpdateOnboardingPageParams,
): Promise<ServicesProvider> => {
	const response = await apiClient.post<ServicesProvider>(
		configurationEndpoints.updateOnboardingPage(servicesProviderId),
		params,
	);
	return response.data;
};

/**
 * Hook for updating the onboarding page for a services provider (supports both forward and backward navigation)
 */
export const useUpdateOnboardingPage = () => {
	const servicesProviderId = useCurrentServicesProviderId();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (params: UpdateOnboardingPageParams) => {
			if (!servicesProviderId) {
				throw new Error('Unable to update onboarding page without a services provider ID.');
			}
			return updateOnboardingPage(servicesProviderId, params);
		},
		onSuccess: (data) => {
			// Invalidate the specific services provider query
			queryClient.invalidateQueries({
				queryKey: queries.servicesProviders.detail(data.id).queryKey,
			});
		},
	});
};
